import styled from 'vue3-styled-components'
/**
 * Summary Typography System
 *
 * Component               | Size  | Weight   | Color       | Classes available
 * ------------------------|-------|----------|-------------|-----------------------------------------------------
 * ExtraHeaderText         | 28px  | bold     | primary     | .center .no-margin-bottom
 * HeaderText              | 20px  | bold     | primary     | .black-text .no-margin-bottom
 * HeaderTextSemiBold      | 20px  | bold     | primary     | .no-margin-bottom
 * BoldText                | 16px  | bold     | primary     | .white-text
 * BoldSmall               | 14px  | bold     | primary     | .white-text .black-text .no-margin-bottom .tiny-text
 *
 * ExtraPrimaryText        | 30px  | normal   | primary     | .black-text .no-margin-bottom .link
 * PrimaryText             | 16px  | normal   | primary     | .black-text .medium .bold .no-margin-bottom .link .item-name .size-18
 * PrimaryTextSmall        | 14px  | normal   | primary     | .black-text .medium .bold .no-margin-bottom
 * PrimaryTextTiny         | 12px  | normal   | primary     | .black-text .medium .bold .no-margin-bottom
 *
 * NormalText              | 16px  | normal   | midGray     | .white-text .mid-gray-new .medium .bold .no-margin-bottom
 * NormalTextSmall         | 14px  | normal   | midGray     | .white-text .black-text .mid-gray-new .medium .bold .no-margin-bottom .no-margin-top .dark-grey .link
 * NormalTextTiny          | 10px  | normal   | midGray     | .mid-gray-new
 * NormalTextTinySecondary | 12px  | normal   | midGray     | .white-text .black-text .mid-gray-new .medium .bold .no-margin-bottom .align-right
 *
 * ErrorText               | 14px  | normal   | danger      | .text-right
 * ActionText              | 16px  | normal   | tertiary    | —
 *
 * Quick usage:
 *   #000 16px → PrimaryText.black-text
 *   #000 14px → PrimaryTextSmall.black-text
 *   #000 12px → PrimaryTextTiny
 *   #666 16px → NormalText.mid-gray-new
 *   #666 14px → NormalTextSmall.mid-gray-new
 *   #666 12px → NormalTextTinySecondary.mid-gray-new
 *
 * Note: font-size/weight from theme functions (headerText/normalText) is non-functional
 * due to curried function pattern. Use class modifiers (.medium/.bold) for weight control.
 */
const textProps = {
  disabled: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  isBold: {
    type: Boolean,
    default: false,
  },
  isCenter: {
    type: Boolean,
    default: false,
  },
}
/**
 * Header styles
 */
/**
 * Font 28px
 */
export const ExtraHeaderText = styled('div')`
  ${({
  theme: {
    headerText,
    primary,
    font: { sizes },
  },
}) => `
    ${headerText(primary, sizes.heading[10])}
    padding-bottom: 20px;

    &.center {
      text-align: center; 
    }

    &.no-margin-bottom {
      padding-bottom: 0;
    }
  `}
`
/**
 * Font 20px
 * */
export const HeaderText = styled('div', textProps)`
  ${({
  disabled,
  theme: {
    primary,
    text,
    headerText,
    newTheme,
    font: { sizes },
  },
}) => `
    ${headerText(primary, sizes.heading[9])}
    padding-bottom: 20px;
    color: ${disabled ? text.placeholder : primary};
    
    &.black-text {
      color: ${newTheme.primary};
    }

    &.no-margin-bottom {
      padding-bottom: 0;
    }
  `}
`
export const HeaderTextSemiBold = styled('div', textProps)`
  ${({
  disabled,
  theme: {
    primary,
    text,
    fontInter,
    font: { sizes },
  },
}) => `
    ${fontInter(sizes.heading[9], primary, 'bold')}
    padding-bottom: 20px;
    color: ${disabled ? text.placeholder : primary};
    
    &.no-margin-bottom {
      padding-bottom: 0;
    }
  `}
`
export const BoldText = styled('div', textProps)`
  ${({ theme: { primary, headerText }, inline }) => `
    ${headerText(primary, '16px')}
    padding-bottom: 10px;
    &.white-text {
      color: #ffffff;
    }
    ${inline ? 'display: inline-block;' : ''}
  `}
`
export const BoldSmall = styled('div')`
  ${({ theme: { primary, headerText, newTheme } }) => `
    ${headerText(primary, '14px')}
    padding-bottom: 10px;
    &.white-text {
      color: #ffffff;
    }
    &.black-text {
      color: ${newTheme.primary};
    }

    &.no-margin-bottom {
      padding-bottom: 0;
    }
    &.tiny-text {
      font-size: 12px;
    }
  `}
`
/**
 * Text styles
 */
/**
 * Font 16px; Mid Gray
 * */
export const NormalText = styled('div', textProps)`
  ${({ theme: { normalText, midGray, newTheme }, isBold, inline, isCenter }) => `
    ${normalText(midGray, '16px')}
    margin-bottom: 10px;
    &.white-text {
      color: #ffffff;
    }
    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.medium {
      font-weight: 500;
    }
    &.bold {
      font-weight: 600;
    }
    &.mid-gray-new {
      color: ${newTheme.midGray};
    }
    ${isBold ? 'font-weight: 700;' : ''}
    ${inline ? 'display: inline-block;' : ''}
    ${isCenter ? 'text-align: center;' : ''}
  `}
`
/**
 * Font 14px; Mid Gray
 * */
export const NormalTextSmall = styled('div')`
  ${({ theme: { normalText, midGray, newTheme } }) => `
    ${normalText(midGray, '14px')}
    margin-bottom: 10px;
    &.white-text {
      color: #ffffff;
    }
    &.black-text {
      color: ${newTheme.primary};
    }
    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.no-margin-top {
      margin-top: 0;
    }
    &.dark-grey {
      color: #3D3D3D;
    }
    &.mid-gray-new {
      color: ${newTheme.midGray};
    }
    &.medium {
      font-weight: 500;
    }
    &.bold {
      font-weight: 600;
    }
    &.link {
      color: ${({ theme: { midGray } }) => midGray};
      text-decoration: underline;
      cursor: pointer;
    }
  `}
`
/**
 * Font 10px; Mid Gray
 * */
export const NormalTextTiny = styled('div')`
  ${({ theme: { normalText, midGray, newTheme } }) => `
    ${normalText(midGray, '10px')}
    &.mid-gray-new {
      color: ${newTheme.midGray};
    }
  `}
`

/**
 * Font 10px; Mid Gray
 * */
export const NormalTextTinySecondary = styled('div')`
  ${({ theme: { normalText, midGray, newTheme } }) => `
    ${normalText(midGray, '12px')}
    margin-bottom: 10px;
    &.white-text {
      color: #ffffff;
    }
    &.black-text {
      color: ${newTheme.primary};
    }
    &.mid-gray-new {
      color: ${newTheme.midGray};
    }
    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.medium {
      font-weight: 500;
    }
    &.bold {
      font-weight: 600;
    }
    &.align-right {
      text-align: right;
    }
  `}
`

/**
 * Font 12px; primary (old) or black (new) via .black-text
 * */
export const PrimaryTextTiny = styled('div')`
  ${({ theme: { newTheme, primary } }) => `
    font-size: 12px;
    color: ${primary};
    margin-bottom: 10px;
    &.black-text {
      color: ${newTheme.primary};
    }
    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.medium {
      font-weight: 500;
    }
    &.bold {
      font-weight: 600;
    }
  `}
`


/**
 * Font 16px; Mid Gray
 * */
export const ExtraPrimaryText = styled('div')`
  ${({ theme: { normalText, primary, newTheme } }) => `
    ${normalText(primary, '30px')}
    margin-bottom: 10px;

    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.link {
      color: ${({ theme: { primary } }) => primary};
      text-decoration: underline;
      cursor: pointer;
    }
    &.black-text {
      color: ${newTheme.primary};
    }
  `}
`

export const PrimaryText = styled('div')`
  ${({ theme: { normalText, primary, newTheme } }) => `
    ${normalText(primary, '16px')}
    margin-bottom: 10px;

    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.link {
      color: ${({ theme: { primary } }) => primary};
      text-decoration: underline;
      cursor: pointer;
    }
    &.black-text {
      color: ${newTheme.primary};
    }
    &.medium {
      font-weight: 500;
    }
    &.bold {
      font-weight: 600;
    }
    &.size-18 {
      font-size: 18px;
    }
    &.item-name {
      flex: 1;
      min-width: 0;
    }
  `}
`

export const PrimaryTextSmall = styled('div')`
  ${({ theme: { normalText, primary, newTheme } }) => `
    ${normalText(primary, '14px')}
    margin-bottom: 10px;

    &.black-text {
      color: ${newTheme.primary};
    }
    &.no-margin-bottom {
      margin-bottom: 0;
    }
    &.medium {
      font-weight: 500;
    }
    &.bold {
      font-weight: 600;
    }
    a {
      color: ${primary};
      text-decoration: underline;
      font-size: 14px;
    }
  `}
`

export const ErrorText = styled('div')`
  ${({ theme: { normalText, danger } }) => `
    ${normalText(danger, '14px')}
    &.text-right {
      text-align: right;
    }
  `}
`

export const ActionText = styled('div')`
  ${({ theme: { normalText, chart: {  block: { tertiary } } } }) => `
    ${normalText(tertiary, '16px')};
  `}
`